/**
 * Connected Apps (plans/mcp-oauth.md Phase 8; REQ-28, REQ-29): the AI apps
 * the stasher has connected to Stash over MCP. Each can be disconnected after
 * an inline confirmation; the app keeps working until its current access
 * token expires (at most an hour), then has to be signed in again.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Plug } from 'lucide-react';
import type { OAuthConnection } from '@stash/shared-types';
import { useAppShell } from '../app-shell';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { connectionsService } from '../../services/connections-service';

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; connections: OAuthConnection[] };

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function ConnectionRow({
  connection,
  onRevoked,
}: {
  connection: OAuthConnection;
  onRevoked: (id: string) => void;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = `connection-${connection.id}`;

  const revoke = async (): Promise<void> => {
    setRevoking(true);
    setError(null);
    const result = await connectionsService.revoke(connection.id);
    setRevoking(false);
    setConfirming(false);
    if (result.ok || result.error.kind === 'notFound') {
      onRevoked(connection.id);
      return;
    }
    setError(`Could not disconnect ${connection.clientName}. Please try again.`);
  };

  return (
    <li aria-labelledby={titleId}>
      <Card size="sm">
        <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Plug size={18} className="mt-0.5 text-muted-foreground" aria-hidden="true" />
            <div>
              <div id={titleId} className="font-semibold text-foreground">{connection.clientName}</div>
              <div className="text-xs text-muted-foreground">
                Connected <time dateTime={connection.connectedAt}>{formatDate(connection.connectedAt)}</time>
                {' · '}
                Last used <time dateTime={connection.lastUsedAt}>{formatDate(connection.lastUsedAt)}</time>
              </div>
              {error && (
                <div role="alert" className="mt-1 text-xs text-destructive">{error}</div>
              )}
            </div>
          </div>
          {confirming ? (
            <div className="flex items-center gap-2">
              <span className="text-sm">Disconnect {connection.clientName}?</span>
              <Button variant="destructive" size="sm" disabled={revoking} onClick={() => void revoke()}>
                Yes, disconnect
              </Button>
              <Button variant="ghost" size="sm" disabled={revoking} onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
              Disconnect
            </Button>
          )}
        </CardContent>
      </Card>
    </li>
  );
}

const ConnectedAppsPage = (): React.JSX.Element => {
  const { setBreadcrumb } = useAppShell();
  useEffect(() => {
    setBreadcrumb(['Connected Apps']);
    return () => setBreadcrumb([]);
  }, [setBreadcrumb]);

  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(async (): Promise<void> => {
    setState({ status: 'loading' });
    const result = await connectionsService.list();
    setState(result.ok ? { status: 'loaded', connections: result.data } : { status: 'error' });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const removeConnection = (id: string): void => {
    setState(prev =>
      prev.status === 'loaded'
        ? { status: 'loaded', connections: prev.connections.filter(c => c.id !== id) }
        : prev,
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Connected Apps</h1>
        <p className="text-sm text-muted-foreground">
          AI apps you have connected to your Stash. They can read what you can see in Stash.
          Disconnecting one stops it within an hour; to use it again you will need to sign in from that app.
        </p>
      </div>

      {state.status === 'loading' && (
        <div role="status" className="text-sm text-muted-foreground">Loading connected apps…</div>
      )}

      {state.status === 'error' && (
        <div role="alert" className="flex items-center gap-3 text-sm text-destructive">
          <span>Could not load your connected apps.</span>
          <Button variant="outline" size="sm" onClick={() => void load()}>Try again</Button>
        </div>
      )}

      {state.status === 'loaded' && state.connections.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No connected apps yet. When you connect an AI app such as Claude or ChatGPT to Stash, it appears here.
        </p>
      )}

      {state.status === 'loaded' && state.connections.length > 0 && (
        <ul className="flex flex-col gap-2">
          {state.connections.map(connection => (
            <ConnectionRow key={connection.id} connection={connection} onRevoked={removeConnection} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default ConnectedAppsPage;
